import {createDraftSafeSelector} from '@reduxjs/toolkit';
import {createSelector} from 'reselect';

import consentTypes from '../../consts/consents/consentTypes';
import deviceTypes from '../../consts/device/deviceTypes';
import deviceConnectionTypeService from '../../services/device/deviceConnectionTypeService';
import localizationLanguageService from '../../services/localization/localizationLanguageService';

const selectSessionState = (state) => state.session;

const selectIsDevicesInitialized = createDraftSafeSelector(selectSessionState, (state) => state.isDevicesInitialized);

const selectConsents = createDraftSafeSelector(selectSessionState, (state) => state.consents);

const selectConsentsByType = (type) =>
    createDraftSafeSelector(selectConsents, (consents) => consents.filter((item) => item.type === type));

const selectNotMandatoryConsents = createDraftSafeSelector(
    selectConsentsByType(consentTypes.NOT_MANDATORY),
    (notMandatoryConsents) => notMandatoryConsents
);

const selectMandatoryConsents = createDraftSafeSelector(
    selectConsentsByType(consentTypes.MANDATORY),
    (mandatoryConsents) => mandatoryConsents
);

const selectMandatoryConsentsFlags = createDraftSafeSelector(selectMandatoryConsents, (mandatoryConsents) =>
    mandatoryConsents.map((item) => item.consentFlags).flat()
);

const selectConsentsFlagsValues = createDraftSafeSelector(
    selectSessionState,
    (state) => state.consentsFlagsValues || {}
);

const selectIsMandatoryConsentsAccepted = createDraftSafeSelector(
    [selectConsentsFlagsValues, selectMandatoryConsentsFlags],
    (consentsFlagsValues, mandatoryConsentsFlags) => mandatoryConsentsFlags.every((flag) => consentsFlagsValues[flag])
);

const selectInitialConnectionType = createSelector(selectSessionState, (state) => state.initialConnectionType);

const selectIsWiFiInitialConnectionType = createDraftSafeSelector(selectInitialConnectionType, (connectionType) =>
    deviceConnectionTypeService.isWiFi(connectionType)
);

const selectIsBleInitialConnectionType = createDraftSafeSelector(selectInitialConnectionType, (connectionType) =>
    deviceConnectionTypeService.isBle(connectionType)
);

const selectIsSidewalkInitialConnectionType = createDraftSafeSelector(selectInitialConnectionType, (connectionType) =>
    deviceConnectionTypeService.isSidewalk(connectionType)
);

const selectIsCellularInitialConnectionType = createDraftSafeSelector(selectInitialConnectionType, (connectionType) =>
    deviceConnectionTypeService.isCellular(connectionType)
);

const selectIsConsentsReceiveInProgress = createDraftSafeSelector(
    selectSessionState,
    (state) => state.isConsentsReceiveInProgress
);

const selectIsUpdateConsentsInProgress = createDraftSafeSelector(
    selectSessionState,
    (state) => state.isUpdateConsentsInProgress
);

const selectCaptivePortalProgress = createDraftSafeSelector(selectSessionState, (state) => state.captivePortalProgress);

const selectInitialCodentify = createDraftSafeSelector(selectSessionState, (state) => state.initialCodentify);

const selectInitialDeviceType = createDraftSafeSelector(selectSessionState, (state) => state.initialDeviceType);

const selectInitialDusn = createDraftSafeSelector(selectSessionState, (state) => state.initialDusn);

const selectInitialHolderDusn = createDraftSafeSelector(selectSessionState, (state) => state.initialHolderDusn);

const selectIsIlumaI2InitialDeviceType = createDraftSafeSelector(
    selectInitialDeviceType,
    (deviceType) => deviceType === deviceTypes.ILUMA_I2_CHARGER
);

const selectIsIlumaI5InitialDeviceType = createDraftSafeSelector(
    selectInitialDeviceType,
    (deviceType) => deviceType === deviceTypes.ILUMA_I5_CHARGER
);

const selectQrCodentify = createDraftSafeSelector(selectSessionState, (state) => state.qrCodentify);

const selectActiveDeviceNavigationMenuType = createDraftSafeSelector(
    selectSessionState,
    (state) => state.activeDeviceNavigationMenuType
);

const selectShouldSetupGuideBeShown = createDraftSafeSelector(
    selectSessionState,
    (state) => state.shouldSetupGuideBeShown
);

const selectShouldRedirectToHome = createDraftSafeSelector(selectSessionState, (state) => state.shouldRedirectToHome);

const selectLanguageCode = createDraftSafeSelector(selectSessionState, (state) =>
    localizationLanguageService.handleLanguageCode(state.languageCode)
);

const selectLanguageSwitcherValue = createDraftSafeSelector(selectSessionState, (state) => state.languageCode);

const selectIsFirmwareMock = createDraftSafeSelector(selectSessionState, (state) => state.isFirmwareMock);

const selectIsPushNotificationPageVisible = createDraftSafeSelector(
    selectSessionState,
    (state) => state.isPushNotificationPageVisible
);

const selectIsGeoRestrictionEnabled = createDraftSafeSelector(
    selectSessionState,
    (state) => state.isGeoRestrictionEnabled
);
const selectIsHomeMenuExpanded = createDraftSafeSelector(selectSessionState, (state) => state.isHomeMenuExpanded);
const selectIsUwmFullScreenRequired = createDraftSafeSelector(
    selectSessionState,
    (state) => state.isUwmFullScreenRequired
);

export const selectIsUsageConcentAccepted = createDraftSafeSelector(
    selectSessionState,
    (state) => state.isUsageConcentAccepted
);

const selectIsExperienceScoreConsentsAccepted = createDraftSafeSelector(
    selectSessionState,
    (state) => state?.isExperienceScoreConsentsAccepted
);
const selectIsSecondWiFiConnectionActive = createDraftSafeSelector(
    selectSessionState,
    (state) => state?.isSecondWifiConnectionActive
);
const selectIsUpdateSubscriptionInProgress = createDraftSafeSelector(
    selectSessionState,
    (state) => state?.isUpdateSubscriptionInProgress
);
const selectDisabledNotificationTypes = createDraftSafeSelector(
    selectSessionState,
    (state) => state.disabledNotificationTypes || {}
);

const selectIsSubscriptionEnabled = createDraftSafeSelector(selectSessionState, (state) => state.isSubscriptionEnabled);

const selectIsIncludeEmptyPuff = createDraftSafeSelector(selectSessionState, (state) => state?.isIncludeEmptyPuff);

const selectIsUserSessionInitialized = createDraftSafeSelector(
    selectSessionState,
    (state) => state?.isUserSessionInitialized
);

const selectIsSessionValidationInProgress = createDraftSafeSelector(
    selectSessionState,
    (state) => state.isSessionValidationInProgress
);

const selectIsWiFiConfiguringError = createDraftSafeSelector(
    selectSessionState,
    (state) => state.isWiFiConfiguringError
);

const selectIsOnboardingInProgress = createDraftSafeSelector(
    selectSessionState,
    (state) => state.isOnboardingInProgress
);

export {
    selectActiveDeviceNavigationMenuType,
    selectCaptivePortalProgress,
    selectConsentsByType,
    selectConsentsFlagsValues,
    selectDisabledNotificationTypes,
    selectInitialCodentify,
    selectInitialConnectionType,
    selectInitialDeviceType,
    selectInitialDusn,
    selectInitialHolderDusn,
    selectIsBleInitialConnectionType,
    selectIsCellularInitialConnectionType,
    selectIsConsentsReceiveInProgress,
    selectIsDevicesInitialized,
    selectIsExperienceScoreConsentsAccepted,
    selectIsFirmwareMock,
    selectIsGeoRestrictionEnabled,
    selectIsHomeMenuExpanded,
    selectIsIlumaI2InitialDeviceType,
    selectIsIlumaI5InitialDeviceType,
    selectIsIncludeEmptyPuff,
    selectIsMandatoryConsentsAccepted,
    selectIsOnboardingInProgress,
    selectIsPushNotificationPageVisible,
    selectIsSecondWiFiConnectionActive,
    selectIsSessionValidationInProgress,
    selectIsSidewalkInitialConnectionType,
    selectIsSubscriptionEnabled,
    selectIsUpdateConsentsInProgress,
    selectIsUpdateSubscriptionInProgress,
    selectIsUserSessionInitialized,
    selectIsUwmFullScreenRequired,
    selectIsWiFiConfiguringError,
    selectIsWiFiInitialConnectionType,
    selectLanguageCode,
    selectLanguageSwitcherValue,
    selectMandatoryConsents,
    selectMandatoryConsentsFlags,
    selectNotMandatoryConsents,
    selectQrCodentify,
    selectShouldRedirectToHome,
    selectShouldSetupGuideBeShown,
};
